
import { Facture, Devis, LigneItem } from "@/types";
import { estEncoreBrouillon } from "@/lib/facture-snapshot";

const DRAFT_PREFIX = "invoice_draft_";
const EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * UN DOCUMENT CLOS N'A PAS DE BROUILLON LOCAL.
 *
 * Le défaut, constaté le 9 septembre 2026 en préproduction : il suffisait
 * d'OUVRIR la facture 20180281 — payée, verrouillée, gelée — pour qu'un
 * brouillon soit écrit dans le navigateur au bout d'une seconde. À la
 * réouverture, l'éditeur le restaurait par-dessus les données du serveur, et
 * le PDF changeait : « Conditions : Paiement reçu via Stripe Checkout »
 * devenait « Conditions : À réception », l'échéance suivait.
 *
 * Aucune écriture en base, aucun snapshot touché — et pourtant le document
 * imprimé n'était plus le même. L'immuabilité se perdait dans le navigateur.
 *
 * Le garde est ICI, dans le magasin lui-même, et non dans chaque écran : cinq
 * appelants l'auraient oublié tôt ou tard, et c'est exactement le piège qui
 * s'est déjà refermé cinq fois dans ce dépôt. « estEncoreBrouillon » reste la
 * règle unique, partagée par l'application, le générateur et la reprise.
 *
 * « getDraft » ne se contente pas d'ignorer un brouillon interdit : il
 * l'EFFACE. Ceux qui traînent déjà dans les navigateurs disparaissent ainsi
 * d'eux-mêmes, sans qu'il faille demander quoi que ce soit à personne.
 */
function documentClos(d: Partial<DraftData> | null | undefined): boolean {
    if (!d) return false;
    return !estEncoreBrouillon({
        statut: d.statut ?? null,
        isLocked: d.isLocked ?? null,
        snapshotGeleLe: (d as { snapshotGeleLe?: string | null }).snapshotGeleLe ?? null,
    });
}

export interface DraftData {
    items: LigneItem[];
    clientId?: string;
    dateEmission?: string;
    echeance?: string;
    conditions?: string;
    notes?: string;
    statut?: string;
    numero?: string;
    remiseGlobale?: number;
    remiseGlobaleType?: 'pourcentage' | 'montant';
    isLocked?: boolean;

    // Config specifique
    defaultTva?: number;
    showDateColumn?: boolean;
    showTTCColumn?: boolean;
    showQuantiteColumn?: boolean;
    showTvaColumn?: boolean;
    showRemiseColumn?: boolean;
    showOptionalFields?: boolean;
    discountEnabled?: boolean;
    discountType?: 'pourcentage' | 'montant';
    conditionsPaiement?: string;

    updatedAt: number;
}

export const saveDraft = (id: string, data: Partial<DraftData>) => {
    try {
        const key = `${DRAFT_PREFIX}${id || 'new'}`;

        // Read existing to merge (Crucial for Partial updates from Desktop that doesn't have all fields)
        const existingRaw = localStorage.getItem(key);
        const existing = existingRaw ? JSON.parse(existingRaw) : {};

        const payload: DraftData = {
            ...existing,
            ...data,
            items: data.items || existing.items || [],
            updatedAt: Date.now()
        };

        // Le statut et le verrou peuvent n'être que dans l'un des deux : on
        // juge sur la fusion, jamais sur le seul correctif reçu.
        if (documentClos(payload)) {
            // Et on efface ce qui aurait été écrit avant ce garde.
            localStorage.removeItem(key);
            return;
        }
        console.log(`[STORAGE] Saving draft for key: ${key}`, {
            source: data.items ? 'Update' : 'Patch',
            items: payload.items?.length,
            tva: payload.defaultTva
        });
        localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
        console.error("[STORAGE] Failed to save draft", e);
    }
};

export const getDraft = (id: string): DraftData | null => {
    try {
        const key = `${DRAFT_PREFIX}${id || 'new'}`;
        console.log(`[STORAGE] Reading draft for key: ${key}`);
        const raw = localStorage.getItem(key);
        if (!raw) {
            console.log(`[STORAGE] No draft found for key: ${key}`);
            return null;
        }

        const data = JSON.parse(raw) as DraftData;

        // Expiration check
        if (Date.now() - data.updatedAt > EXPIRATION_MS) {
            localStorage.removeItem(key);
            return null;
        }

        // Un brouillon portant sur un document clos n'aurait jamais dû être
        // écrit. On ne l'ignore pas : on le retire.
        if (documentClos(data)) {
            localStorage.removeItem(key);
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

export const clearDraft = (id: string) => {
    try {
        const key = `${DRAFT_PREFIX}${id || 'new'}`;
        localStorage.removeItem(key);
    } catch (e) {
        // ignore
    }
};
